import { yupResolver } from "@hookform/resolvers/yup";
import {
  Button,
  Typography,
  Link,
  useMediaQuery,
  Box,
  Alert,
  AlertTitle,
  CircularProgress,
  Container,
} from "@mui/material";
import axios from "axios";
import axiosRetry from "axios-retry";
import PropTypes from "prop-types";
import { useState, useRef, useEffect, useCallback } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { getPreChatChoices } from "../../preChatChoices";
import getValidationSchema from "../../getValidationSchema";
import GenesysPlatformService from "../../services/GenesysPlatformService";
import SurveyQuestion from "../shared/SurveyQuestion";
import SubnetworkOptions from "./SubnetworkOptions";
import { SUBNETWORKS } from "../../subnetworks";
import SurveyAccordion from "./SurveyAccordion";
import SurveyZipCode from "./SurveyZipCode";
import ChatDialogueIcon from "../icons/ChatDialogueIcon";
import { replaceMainSiteLinkHostname } from "../../services/Utils";
import { useTheme } from "@mui/material/styles";
import { serializePreChatSurvey } from "../../services/Utils";
import * as Sentry from "@sentry/react";
import SurveyPhoneNumberQuestion from "../shared/SurveyPhoneNumberQuestion";
import useRecaptcha from "../../hooks/useRecaptcha";
import { useChatState } from "../../hooks/useChatState";
import surveyLinkStyles from "../../styles/surveyLinkStyles";

const SurveyForm = ({ interactionIdKey, filteredParams = {} }) => {
  const { t, i18n } = useTranslation(["preChat", "common"]);
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("lg"));
  const { thoughtsOfSuicideChoices, upsetScaleChoices } = getPreChatChoices(t);
  const [platformServiceConfig, setPlatformServiceConfig] = useState(null);
  const isEnglish = i18n.language.includes("en");
  const validationSchema = getValidationSchema(t);
  const [isFormSubmitted, setIsFormSubmitted] = useState(false);
  const startTimeRef = useRef(Date.now());
  const completedFieldsRef = useRef([]);
  const [formStarted, setFormStarted] = useState(false);
  const { executeRecaptcha } = useRecaptcha();
  const { setIsChatActive } = useChatState();
  const [retryCount, setRetryCount] = useState(0);

  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
    clearErrors,
    watch,
    setValue,
    setError,
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      modality: "web",
      zipCode: "",
      thoughtsOfSuicide: "",
      upsetAmount: "",
      name: "",
      age: "",
      gender: "",
      gender_unlisted: "",
      transgender: "",
      mainConcerns: [],
      mainConcerns_unlisted: "",
      phoneNumber: "",
      subnetwork: "",
    },
  });

  const showSubnetworkOptions = isEnglish;

  const zipCodeRef = useRef(null);
  const zipCodeValue = useWatch({
    control,
    name: "zipCode",
  });
  useEffect(() => {
    if (isZipCodeInWashington(zipCodeValue)) return;

    if (watch("subnetwork") === SUBNETWORKS.NATIVE) {
      setValue("subnetwork", "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zipCodeValue]);

  useEffect(() => {
    if (!showSubnetworkOptions) {
      setValue("subnetwork", "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showSubnetworkOptions]);
  const thoughtsOfSuicideRef = useRef(null);
  const upsetAmountRef = useRef(null);

  const scrollToFirstError = useCallback(() => {
    const offset = isDesktop ? 150 : 200;

    const scrollWithOffset = (ref) => {
      const elementPosition = ref.current.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.scrollY - offset;
      window.scrollTo({
        top: offsetPosition,
        behavior: "smooth",
      });
    };

    if (errors.zipCode) {
      scrollWithOffset(zipCodeRef);
    } else if (errors.thoughtsOfSuicide) {
      scrollWithOffset(thoughtsOfSuicideRef);
    } else if (errors.upsetAmount) {
      scrollWithOffset(upsetAmountRef);
    }
  }, [errors, isDesktop]);

  // fn. for Native and strong checkbox
  const isZipCodeInWashington = (zipCode) => {
    if (!zipCode || zipCode.length !== 5) return false;
    const numericZip = parseInt(zipCode, 10);
    return numericZip >= 98001 && numericZip <= 99403;
  };

  axiosRetry(axios, {
    retries: 5,
    retryDelay: axiosRetry.exponentialDelay,
    retryCondition: (error) => {
      return (
        axiosRetry.isNetworkError(error) ||
        axiosRetry.isRetryableError(error) ||
        (error.response &&
          error.response.status >= 500 &&
          error.response.status < 600)
      );
    },
  });

  axios.interceptors.request.use((config) => {
    if (config["axios-retry"] && config["axios-retry"].retryCount > 0) {
      setRetryCount(config["axios-retry"].retryCount);
    }
    return config;
  });

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const pushToDataLayer = (eventName, eventData) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: eventName,
      ...eventData,
    });
  };

  const handleOnSubmit = async (data) => {
    clearErrors();
    setRetryCount(0);

    const endTime = Date.now();
    const duration = (endTime - startTimeRef.current) / 1000;

    pushToDataLayer("survey_submit", {
      event_category: "988 Lifeline Survey",
      event_label: "Pre-Chat Survey",
      duration: duration,
      completed_fields_count: completedFieldsRef.current.length,
      completed_fields_list: completedFieldsRef.current.join(", "),
      transport_type: "beacon",
    });

    let recaptchaToken = "";
    try {
      recaptchaToken = await executeRecaptcha("submit");
    } catch (error) {
      Sentry.captureException(error, {
        tags: {
          action: "recaptcha_execution",
          formContext: "survey_submit",
        },
      });
    }

    setIsFormSubmitted(true);

    let cleanedData = {};
    for (const [key, value] of Object.entries(data)) {
      if (!(key.endsWith("_unlisted") && value === undefined)) {
        cleanedData[key] = value;
      }
    }
    cleanedData = {
      ...cleanedData,
      surveyID: filteredParams?.surveyID,
      recaptchaToken,
      interactionIdKey,
      modality: "web",
      locale: normalizeLanguage(i18n.language),
    };

    const defaultName = isEnglish ? "Anonymous" : "Anónimo";

    if (!cleanedData.name) {
      cleanedData.name = defaultName;
    }

    const PostapiEndpoint = import.meta.env.VITE_LIFELINE_CHAT_SURVEY_API_URL;

    const serializedData = serializePreChatSurvey(cleanedData);

    try {
      const surveyResponse = await axios.post(PostapiEndpoint, serializedData, {
        timeout: 30000,
      });

      if (surveyResponse.status === 201) {
        const redirectUrl = surveyResponse?.data?.redirect_url;

        const isThirdParty = redirectUrl?.startsWith("http");

        if (isThirdParty) {
          window.location.href = redirectUrl;
          return;
        }

        serializedData.client_ip = surveyResponse.data.user_ip;
        serializedData.isp = surveyResponse.data.user_isp;
        serializedData.chat_session_id = surveyResponse.data.chat_session_id;
        serializedData.submit_timestamp = surveyResponse.data.submit_timestamp;
        serializedData.native = surveyResponse.data.native;

        if (
          !serializedData.client_ip ||
          !serializedData.isp ||
          !serializedData.chat_session_id ||
          !serializedData.submit_timestamp
        ) {
          throw new Error("Missing required data from server response.");
        }

        setPlatformServiceConfig({ data: serializedData });
        setIsChatActive(true);
      } else {
        throw new Error(`Unexpected response status: ${surveyResponse.status}`);
      }
    } catch (error) {
      Sentry.captureException(error, {
        tags: {
          component: "SurveyForm",
          action: "form_submission",
        },
        extra: {
          surveyID: filteredParams?.surveyID,
          statusCode: error.response?.status,
          responseData: error.response?.data,
          retryCount: retryCount,
        },
        level: "error",
      });

      setIsFormSubmitted(false);

      setRetryCount(0);

      setError("root.serverError", {
        type: "manual",
        message: t("serverError.message", { ns: "common" }),
      });
      scrollToTop();
    }
  };

  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      scrollToFirstError();
    }
  }, [errors, scrollToFirstError]);

  useEffect(() => {
    if (!formStarted) {
      const handleInteraction = () => {
        if (!formStarted) {
          pushToDataLayer("survey_start", {
            event_category: "988 Lifeline Survey",
            event_label: "Pre-Chat Survey",
          });
          setFormStarted(true);
          removeEventListeners();
        }
      };

      const formFields = document.querySelectorAll("input, select, textarea");
      formFields.forEach((field) => {
        field.addEventListener("focus", handleInteraction, { once: true });
        field.addEventListener("click", handleInteraction, { once: true });
      });

      const removeEventListeners = () => {
        formFields.forEach((field) => {
          field.removeEventListener("focus", handleInteraction);
          field.removeEventListener("click", handleInteraction);
        });
      };

      return removeEventListeners;
    }
  }, [formStarted]);

  const watchedFields = watch();
  useEffect(() => {
    const completedFields = Object.keys(watchedFields).filter(
      (field) => watchedFields[field],
    );
    completedFieldsRef.current = completedFields;
  }, [watchedFields]);

  useEffect(() => {
    const sendDropoffEvent = () => {
      if (!isFormSubmitted && formStarted) {
        const endTime = Date.now();
        const duration = (endTime - startTimeRef.current) / 1000;

        pushToDataLayer("survey_dropoff", {
          event_category: "988 Lifeline Survey",
          event_label: "Pre-Chat Survey",
          duration: duration,
          completed_fields_count: completedFieldsRef.current.length,
          completed_fields_list: completedFieldsRef.current.join(", "),
          transport_type: "beacon",
        });
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        sendDropoffEvent();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange, {
      once: true,
    });

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [isFormSubmitted, formStarted]);

  function normalizeLanguage(language) {
    return language?.startsWith("en") ? "en" : language;
  }

  return (
    <>
      <Container
        maxWidth="md"
        disableGutters
        sx={{ bgcolor: theme.palette.white, borderRadius: "10px" }}
      >
        {!platformServiceConfig && (
          <Box sx={{ px: 1.5, pt: isDesktop ? 6 : 3, mt: isDesktop ? 0 : 3 }}>
            <Typography
              variant="h2"
              sx={{
                fontSize: "25px",
                textAlign: "center",
                whiteSpace: { lg: "nowrap", xs: "normal" },
              }}
            >
              {t("headerText.fillOutInfo")}{" "}
              <Box component="span" sx={{ fontWeight: "800" }}>
                {t("headerText.startChat")}
              </Box>
            </Typography>
            <Typography
              variant="body1"
              sx={{ my: 1, mb: 3, textAlign: "center" }}
            >
              {t("headerText.veteranText")}{" "}
              <Link
                href={t("headerText.vclUrl")}
                underline="always"
                sx={(theme) => surveyLinkStyles(theme)}
                target="_blank"
              >
                {t("headerText.here")}
              </Link>
            </Typography>
          </Box>
        )}

        <Container
          maxWidth="sm"
          disableGutters
          sx={{
            mt: 2,
            pt: isDesktop ? 1 : 0,
            mb: isDesktop ? 13 : 0,
            pb: 1.5,
            px: 1.5,
            bgcolor: "transparent",
          }}
        >
          {platformServiceConfig ? (
            <GenesysPlatformService config={platformServiceConfig} />
          ) : (
            <Box>
              <form onSubmit={handleSubmit(handleOnSubmit)}>
                {errors.root?.serverError && (
                  <Box sx={{ mb: 3 }}>
                    <Alert
                      severity="error"
                      sx={{
                        borderWidth: 1,
                        borderStyle: "solid",
                        borderColor: theme.palette.mediumRed,
                        bgcolor: theme.palette.lightRed,
                        color: "black",
                      }}
                    >
                      <AlertTitle
                        sx={{
                          color: theme.palette.darkRed,
                          fontWeight: "bold",
                          fontSize: "1.25rem",
                        }}
                      >
                        {t("serverError.title", { ns: "common" })}
                      </AlertTitle>
                      {t("serverError.message", { ns: "common" })}
                    </Alert>
                  </Box>
                )}
                <Box ref={zipCodeRef}>
                  <SurveyZipCode
                    name="zipCode"
                    control={control}
                    register={register}
                    errors={errors}
                    spacingBottom={1}
                  />
                </Box>
                <Box ref={thoughtsOfSuicideRef}>
                  <SurveyQuestion
                    errors={errors}
                    question={t("suicideQuestion.title")}
                    options={thoughtsOfSuicideChoices}
                    name="thoughtsOfSuicide"
                    control={control}
                    required
                    spacingTop={1.5}
                    spacingBottom={1.5}
                    namespace="preChat"
                  />
                </Box>
                <Box ref={upsetAmountRef}>
                  <SurveyQuestion
                    errors={errors}
                    question={t("upsetQuestion.title")}
                    options={upsetScaleChoices}
                    name="upsetAmount"
                    control={control}
                    required
                    spacingTop={1.5}
                    spacingBottom={1.5}
                    namespace="preChat"
                  />
                </Box>
                {showSubnetworkOptions && (
                  <SubnetworkOptions
                    control={control}
                    isWashington={isZipCodeInWashington(zipCodeValue)}
                  />
                )}
                <SurveyAccordion control={control} />
                <SurveyPhoneNumberQuestion
                  control={control}
                  name="phoneNumber"
                  errors={errors}
                />
                <Button
                  type="submit"
                  variant="contained"
                  disableElevation={true}
                  disabled={isSubmitting}
                  disableRipple={true}
                  sx={{
                    mt: 3,
                    mb: 2,
                    width: isEnglish ? 200 : 250,
                    height: 60,
                    borderRadius: 50,
                    display: "flex",
                    gap: 1,
                    alignItems: "center",
                    mx: "auto",
                    fontFamily: "Public Sans",
                    fontWeight: "bold",
                    fontSize: 20,
                    bgcolor: theme.palette.primary.main,
                    textTransform: "none",
                    color: theme.palette.white,
                    ":hover": {
                      bgcolor: theme.palette.secondary.dark,
                    },
                    ":focus": {
                      bgcolor: theme.palette.secondary.dark,
                      outline: `1px dashed ${theme.palette.tertiary.main}`,
                      outlineOffset: 4,
                    },
                  }}
                >
                  {isSubmitting ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    <>
                      <ChatDialogueIcon fill="white" />
                      {t("startChat.chatButtonText")}
                    </>
                  )}
                </Button>
                {retryCount > 0 && (
                  <Typography
                    variant="body2"
                    sx={{ textAlign: "center", my: 2 }}
                  >
                    {t("submission.retryMessage", { retryCount })}
                  </Typography>
                )}
                <Typography
                  variant="body1"
                  sx={{ textAlign: "center", mb: 1.5 }}
                >
                  {t("startChat.termsOfServiceText")}{" "}
                  <Link
                    href={replaceMainSiteLinkHostname(
                      t("startChat.termsOfServiceUrl"),
                    )}
                    underline="always"
                    sx={(theme) => surveyLinkStyles(theme)}
                    target="_blank"
                  >
                    {t("startChat.termsOfServiceLink")}
                  </Link>
                </Typography>
                <Typography variant="body1" sx={{ textAlign: "center", mb: 7 }}>
                  <Link
                    href={replaceMainSiteLinkHostname(
                      t("startChat.whatToExpectUrl"),
                    )}
                    underline="always"
                    sx={(theme) => surveyLinkStyles(theme)}
                    target="_blank"
                    rel="noopener"
                  >
                    {t("startChat.whatToExpectLink")}{" "}
                  </Link>
                  {t("startChat.whatToExpectText")}
                </Typography>
              </form>
            </Box>
          )}
        </Container>
      </Container>
    </>
  );
};

SurveyForm.propTypes = {
  interactionIdKey: PropTypes.string,
  filteredParams: PropTypes.object,
};

export default SurveyForm;
